import React from 'react';
import { Form, InputNumber, Select } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react-lite';
import { t } from 'i18next';
import { TankSectionContainer } from './TankSection.styles';
import { EPButton } from '@/components';
import { breakpoints } from '@/utils/util-breakpoints';
import { TANK_OPTIONS } from '../../RoomConfigurationContent.utils';
import { ROOMS } from '@/store/SiteSurveyForm/constants';
import { useMeasurement } from '@/hooks/useMeasurement';

interface TankSectionProps {
  roomId: number;
  roomIndex: number;
  pressureType: string;
  tanks: Array<{
    id: string;
    tankType: string;
    tankValue: number;
  }>;
  onAddTank: (roomId: number) => void;
  onRemoveTank: (roomId: number, tankId: string) => void;
}

const TankSection: React.FC<TankSectionProps> = observer(
  ({ roomId, roomIndex, pressureType, tanks, onAddTank, onRemoveTank }) => {
    const isMobile = window.innerWidth < breakpoints.md;
    const { tunit } = useMeasurement();
    const form = Form.useFormInstance();
    const tankType = Form.useWatch([pressureType, 'rooms', roomIndex, 'newTank', 'tankType'], form);

    return (
      <TankSectionContainer>
        <div className="tank-section-header">
          <div className="tank-section-title">{t('Tanks in the room')}</div>
          <div className="tank-content-section">
            {tanks?.length > 0 && (
              <div className="tank-list-container">
                {tanks.map((tank, index) => (
                  <div key={tank.id} className={`tank-controls auto-width ${isMobile ? 'full-width' : ''}`}>
                    <Form.Item
                      name={[pressureType, ROOMS, roomIndex, 'tanks', index, 'tankType']}
                      className="tank-form-item"
                      rules={[{ required: true, message: t('Please select a type') }]}
                    >
                      <Select
                        className="tank-select"
                        value={tank.tankType}
                        disabled
                        size="large"
                        options={TANK_OPTIONS}
                      />
                    </Form.Item>
                    <div className="tank-action-group">
                      <div className="tank-capacity-input-group">
                        <div className="tank-capacity-input">
                          <Form.Item
                            name={[pressureType, ROOMS, roomIndex, 'tanks', index, 'tankValue']}
                            className="tank-form-item"
                          >
                            <InputNumber
                              min={1}
                              value={tank.tankValue}
                              disabled
                              className="hidden-controls-input-number ant-input-number input-number-centered"
                            />
                          </Form.Item>
                        </div>
                        <div className="capacity-unit">{tunit('Liter').substring(0, 3)}</div>
                      </div>
                      <EPButton
                        className="tank-action-button"
                        icon={<DeleteOutlined />}
                        size="large"
                        onClick={() => onRemoveTank(roomId, tank.id)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className={`tank-controls auto-width ${isMobile ? 'full-width' : ''}`}>
                <Form.Item name={[pressureType, ROOMS, roomIndex, 'newTank', 'tankType']} className="tank-form-item">
                  <Select
                  className="tank-select"
                  size="large"
                  options={TANK_OPTIONS}
                  placeholder={t('Select Type')}
                  allowClear
                />
              </Form.Item>
              <div className="tank-action-group">
                <div className="tank-capacity-input-group">
                  <div className="tank-capacity-input">
                    <Form.Item
                      name={[pressureType, ROOMS, roomIndex, 'newTank', 'tankValue']}
                      className="tank-form-item"
                    >
                      <InputNumber
                        min={1}
                        className="hidden-controls-input-number ant-input-number input-number-centered"
                          placeholder=""
                          stringMode
                          precision={0}
                          disabled={!tankType}
                        />
                      </Form.Item>
                    </div>
                  <div className="capacity-unit">{tunit('Liter').substring(0, 3)}</div>
                </div>
                <EPButton
                  icon={<PlusOutlined />}
                  size="large"
                  onClick={() => onAddTank(roomId)}
                  title={t('Add another tank')}
                  className="add-tank-button"
                />
              </div>
            </div>
          </div>
        </div>
      </TankSectionContainer>
    );
  },
);

export default TankSection;
